import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { listarRecebimentosPedido, deletarRecebimento } from '@/integrations/supabase/recebimentos-pedidos';
import { toast } from 'sonner';
import { Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Mapeamento de formas de pagamento
const PAYMENT_METHODS: Record<string, string> = {
  'pix': 'Pix',
  'boleto': 'Boleto',
  'cartao': 'Cartão',
  'credito': 'Crédito',
  'debito': 'Débito',
  'dinheiro': 'Dinheiro',
  'cheque': 'Cheque',
  'outro': 'Outro'
};

interface ListaRecebimentosProps {
  orderId: string;
  totalPedido: number;
  paidValue?: number;
  onRecebimentoDeleted?: () => void;
}

export function ListaRecebimentos({
  orderId,
  totalPedido,
  paidValue = 0,
  onRecebimentoDeleted
}: ListaRecebimentosProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: recebimentos = [], refetch, isLoading } = useQuery({
    queryKey: ['recebimentos', orderId],
    queryFn: () => listarRecebimentosPedido(orderId)
  });

  // Somar recebimentos manuais + valor pago automaticamente ao criar o pedido
  const recebimentosManuais = recebimentos.reduce((sum, r) => sum + r.valor_recebido, 0);
  const totalRecebido = Math.max(paidValue, recebimentosManuais);
  const saldoDevedor = Math.max(0, totalPedido - totalRecebido);

  const handleDeleteRecebimento = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar este recebimento?')) return;

    setDeletingId(id);
    try {
      const result = await deletarRecebimento(id);
      if (result.ok) {
        toast.success('Recebimento deletado');
        refetch();
        onRecebimentoDeleted?.();
      } else {
        toast.error(result.error || 'Erro ao deletar');
      }
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao deletar');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resumo com estilo Ateliê Pro */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 shadow-sm">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Total do Pedido</p>
          <p className="text-xl font-bold text-purple-900">R$ {totalPedido.toFixed(2)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Recebido
          </p>
          <p className="text-xl font-bold text-emerald-600">R$ {totalRecebido.toFixed(2)}</p>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Saldo Devedor</p>
          <p className={`text-xl font-bold ${saldoDevedor > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
            R$ {saldoDevedor.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Lista de recebimentos */}
      {recebimentos.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="text-purple-300 mb-2">💜</div>
          <p className="text-sm text-purple-600 font-medium">Nenhum recebimento registrado ainda</p>
          <p className="text-xs text-purple-500 mt-1">Registre o primeiro recebimento para acompanhar o pagamento</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recebimentos.map((recebimento, index) => (
            <div
              key={recebimento.id}
              className="flex items-start justify-between p-4 border border-purple-200 rounded-xl bg-gradient-to-r from-purple-50 to-transparent hover:from-purple-100 hover:to-purple-50 transition-colors group"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 text-xs font-bold">
                    {index + 1}
                  </div>
                  <span className="text-base font-semibold text-purple-900">
                    R$ {recebimento.valor_recebido.toFixed(2)}
                  </span>
                  {recebimento.forma_pagamento_id && (
                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      via {PAYMENT_METHODS[recebimento.forma_pagamento_id.toLowerCase()] || recebimento.forma_pagamento_id}
                    </span>
                  )}
                  <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                    {format(parseISO(recebimento.data_recebimento), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                </div>
                {recebimento.observacoes && (
                  <p className="text-xs text-purple-700 mt-2 pl-9 italic">
                    📝 {recebimento.observacoes}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => recebimento.id && handleDeleteRecebimento(recebimento.id)}
                disabled={deletingId === recebimento.id}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {deletingId === recebimento.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
